import { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { palette } from '../../styles/tokens';
import { SlidePanel } from '../../components/common/SlidePanel';
import { AddressAutocomplete } from '../../components/common/AddressAutocomplete';
import type { AddressResult } from '../../components/common/AddressAutocomplete';
import { PhoneInput } from '../../components/common/PhoneInput';
import { DatePicker } from '../../components/common/DatePicker';
import { getGroupedClientFields, getClientFields } from '../../data/clientFieldsConfig';
import { useAuthStore } from '../../stores/authStore';
import type { Client } from '../../types';

const LABEL: React.CSSProperties = { fontSize: 13, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' };
const INPUT: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const };

interface Props {
  isOpen: boolean;
  client: Client | null;
  onClose: () => void;
  onSave: (data: Partial<Client>) => void;
}

export function ClientFormPanel({ isOpen, client, onClose, onSave }: Props) {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [placeId, setPlaceId] = useState<string | null>(null);
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [clientType, setClientType] = useState('individual');
  const [billingEmail, setBillingEmail] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [notes, setNotes] = useState('');
  const [birthday, setBirthday] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- custom_fields is dynamic form JSON; consumer <input> binding needs any here
  const [customFields, setCustomFields] = useState<Record<string, any>>({});
  const [cfExpanded, setCfExpanded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const categoryId = useAuthStore((s) => s.business?.industry ?? null);
  const fieldDefs = getClientFields(categoryId);
  const groupedFields = getGroupedClientFields(categoryId);

  useEffect(() => {
    if (isOpen && client) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- prop-sync form pattern; key-remount refactor out of scope for §1.7
      setName(client.name); setCompany(client.company_name ?? '');
      setEmail(client.email ?? ''); setPhone(client.phone ?? '');
      setAddress(client.address ?? '');
      setPlaceId(client.place_id ?? null);
      setLatitude(client.latitude ?? null);
      setLongitude(client.longitude ?? null);
      setClientType(client.client_type ?? 'individual');
      setBillingEmail(client.billing_email ?? '');
      setTags(client.tags ?? []); setNotes(client.notes ?? '');
      setBirthday(client.birthday ?? '');
      setCustomFields(client.custom_fields ?? {});
      setCfExpanded(false);
    } else if (isOpen) {
      setName(''); setCompany(''); setEmail(''); setPhone(''); setAddress('');
      setPlaceId(null); setLatitude(null); setLongitude(null);
      setClientType('individual'); setBillingEmail(''); setTags([]); setNotes('');
      setBirthday(''); setCustomFields({}); setCfExpanded(false);
    }
    setErr(null); setTagInput('');
  }, [isOpen, client]);

  const handleSave = async () => {
    if (!name.trim()) { setErr('Name is required'); return; }
    setSaving(true); setErr(null);
    // Strip empty custom field values
    const cf: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(customFields)) {
      if (v != null && v !== '' && v !== false) cf[k] = v;
    }
    await onSave({
      name: name.trim(), company_name: company || null, email: email || null,
      phone: phone || null, address: address || null,
      place_id: placeId, latitude, longitude,
      client_type: clientType,
      billing_email: billingEmail || null, tags: tags.length > 0 ? tags : null,
      notes: notes || null,
      birthday: birthday || null,
      custom_fields: cf,
    });
    setSaving(false);
  };

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) { setTags([...tags, t]); setTagInput(''); }
  };

  const F: React.CSSProperties = { marginBottom: 14 };

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose} title={client ? 'Edit Client' : 'New Client'} subtitle={client ? client.name : 'Add a client'}>
      {err && <div style={{ padding: '8px 12px', borderRadius: 6, background: palette.redWash, color: palette.red, fontSize: 12.5, marginBottom: 16 }}>{err}</div>}

      <div style={F}><label style={LABEL}>Name *</label><input style={INPUT} value={name} onChange={e => setName(e.target.value)} placeholder="John Smith" /></div>
      <div style={F}><label style={LABEL}>Company name</label><input style={INPUT} value={company} onChange={e => setCompany(e.target.value)} /></div>
      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Email</label><input type="email" style={INPUT} value={email} onChange={e => setEmail(e.target.value)} /></div>
        <div style={{ flex: 1 }}><label style={LABEL}>Phone</label><PhoneInput value={phone} onChange={setPhone} /></div>
      </div>
      <div style={F}>
        <label style={LABEL}>Address</label>
        <AddressAutocomplete
          initialValue={address}
          types="geocode|establishment"
          onSelect={(r: AddressResult) => {
            setAddress(r.fullAddress);
            setPlaceId(r.placeId);
            setLatitude(r.latitude);
            setLongitude(r.longitude);
          }}
        />
      </div>
      <div style={F}><label style={LABEL}>Birthday (optional)</label>
        <div style={{ width: 220 }}>
          <DatePicker value={birthday || null} onChange={(v) => setBirthday(v ?? '')} maxDate={new Date().toISOString().split('T')[0]} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Type</label>
          <select style={INPUT} value={clientType} onChange={e => setClientType(e.target.value)}>
            <option value="individual">Individual</option>
            <option value="company">Company</option>
          </select>
        </div>
        <div style={{ flex: 1 }}><label style={LABEL}>Billing email</label><input type="email" style={INPUT} value={billingEmail} onChange={e => setBillingEmail(e.target.value)} placeholder="Separate from contact" /></div>
      </div>

      {/* Tags */}
      <div style={F}>
        <label style={LABEL}>Tags</label>
        <div style={{ display: 'flex', gap: 4, marginBottom: 6, flexWrap: 'wrap' }}>
          {tags.map(tag => (
            <span key={tag} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: palette.accentWash, color: palette.accentDeep }}>
              {tag}
              <button onClick={() => setTags(tags.filter(t => t !== tag))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: palette.accentDeep, padding: 0, display: 'flex' }}><X size={10} /></button>
            </span>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <input style={{ ...INPUT, flex: 1 }} value={tagInput} onChange={e => setTagInput(e.target.value)} placeholder="Add tag..."
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }} />
          <button onClick={addTag} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: palette.accent, color: palette.white, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Add</button>
        </div>
      </div>

      {/* Custom fields (industry-specific) */}
      {fieldDefs.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <button
            onClick={() => setCfExpanded(!cfExpanded)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0', fontFamily: 'inherit', fontSize: 13, fontWeight: 500, color: palette.g600 }}
          >
            Industry details (optional)
            {cfExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {cfExpanded && groupedFields.map(group => (
            <div key={group.section} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: palette.g400, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8, marginTop: 8 }}>{group.section}</div>
              {group.fields.map(field => (
                <div key={field.key} style={{ marginBottom: 10 }}>
                  {field.type !== 'boolean' && (
                    <label style={LABEL}>{field.label}{field.required ? ' *' : ''}</label>
                  )}
                  {field.type === 'text' && (
                    <input style={INPUT} value={customFields[field.key] ?? ''} onChange={e => setCustomFields(p => ({ ...p, [field.key]: e.target.value }))} placeholder={field.placeholder} />
                  )}
                  {field.type === 'textarea' && (
                    <textarea style={{ ...INPUT, minHeight: 50, resize: 'vertical' }} value={customFields[field.key] ?? ''} onChange={e => setCustomFields(p => ({ ...p, [field.key]: e.target.value }))} placeholder={field.placeholder} />
                  )}
                  {field.type === 'number' && (
                    <input type="number" style={{ ...INPUT, width: 140 }} value={customFields[field.key] ?? ''} onChange={e => setCustomFields(p => ({ ...p, [field.key]: e.target.value }))} placeholder={field.placeholder} />
                  )}
                  {field.type === 'select' && (
                    <select style={INPUT} value={customFields[field.key] ?? ''} onChange={e => setCustomFields(p => ({ ...p, [field.key]: e.target.value }))}>
                      <option value="">Select...</option>
                      {(field.options ?? []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  )}
                  {field.type === 'date' && (
                    <div style={{ width: 220 }}>
                      <DatePicker value={customFields[field.key] || null} onChange={(v) => setCustomFields(p => ({ ...p, [field.key]: v ?? '' }))} />
                    </div>
                  )}
                  {field.type === 'boolean' && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: palette.g800 }}>
                      <input type="checkbox" checked={!!customFields[field.key]} onChange={e => setCustomFields(p => ({ ...p, [field.key]: e.target.checked }))} />
                      {field.label}
                    </label>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <div style={F}><label style={LABEL}>Notes</label><textarea style={{ ...INPUT, minHeight: 60, resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)} /></div>

      {/* Source (read-only on edit) */}
      {client && (
        <div style={{ fontSize: 12, color: palette.g400, marginBottom: 14 }}>Source: <strong>{client.source}</strong></div>
      )}

      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: saving ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{saving ? 'Saving...' : client ? 'Update' : 'Create'}</button>
      </div>
    </SlidePanel>
  );
}
